import React, { useState } from 'react';
import { push, ref, set, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import { saveMarketingLink } from '../services/marketingLinks';
import { celebrateTaskDone } from '../utils/celebrateTask';

// Brochures, mailers and just sold postcards, all in Resi:
// 1. Marketing manager pastes the link on "Brochures" or "Mailers" and presses Send. That task is done and
//    "Review and Approve ..." appears for the agent (and co-agent, if there is one).
// 2. Whichever agent approves, that review task is done and "Order ... for Printing" appears for the marketing manager.
// The local assistant isn't part of this.

const ITEMS = [
  {
    key: 'brochures',
    create: /^(create )?brochures$/i,
    review: 'Review and Approve Brochures',
    reviewPattern: /^review and approve brochures$/i,
    order: 'Order Brochures for Printing',
    orderPattern: /^order brochures for printing$/i,
  },
  {
    key: 'mailers',
    create: /^(create )?mailers$/i,
    review: 'Review and Approve Mailers',
    reviewPattern: /^review and approve mailers$/i,
    order: 'Order Mailers for Printing',
    orderPattern: /^order mailers for printing$/i,
  },
  {
    // Created only when the deal closes (see releaseClosedTasks).
    key: 'just sold postcards',
    linkKey: 'justSoldPostcards',
    label: 'Just Sold Postcards',
    create: /^(create )?just sold postcards$/i,
    review: 'Review and Approve Just Sold Postcards',
    reviewPattern: /^review and approve just sold postcards$/i,
    order: 'Order Just Sold Postcards for Printing',
    orderPattern: /^order just sold postcards for printing$/i,
  },
];

const nowIso = () => new Date().toISOString();
const todayDate = () => new Date().toISOString().slice(0, 10);

const newTask = (name, role, extra = {}) => ({
  name,
  role,
  type: 'Task',
  appliesTo: 'All',
  anchor: null,
  offsetDays: 0,
  shared: false,
  action: null,
  mode: 'task',
  dueDate: todayDate(),
  status: 'pending',
  completedBy: null,
  completedAt: null,
  createdBy: null,
  createdAt: nowIso(),
  ...extra,
});

const itemFor = (name) => ITEMS.find((i) => i.create.test((name || '').trim()) || i.reviewPattern.test((name || '').trim()) || i.orderPattern.test((name || '').trim())) || null;

export const isMaterialTask = (name) => Boolean(itemFor(name));

const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', opacity: 0.5, cursor: 'not-allowed' };
const input = { flex: '1 1 260px', padding: '8px 10px', borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 13, minWidth: 200 };

export default function MaterialApprovalFlow({ task, txId, onChanged }) {
  const taskName = (task.name || task.title || '').trim();
  const [link, setLink] = useState(task.materialLink || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const item = itemFor(taskName);
  if (!item || !txId) return null;

  const taskPath = `transactions/${txId}/tasks/${task.id}`;

  // Marketing manager: save the link, finish this task, and send it to the agent for approval.
  const sendForApproval = async (e) => {
    if (busy || task.status === 'completed') return;
    const node = e?.currentTarget || null;
    const value = link.trim();
    if (!/^https?:\/\//i.test(value)) { setError('Paste the full link, starting with https://'); return; }
    setBusy(true); setError('');
    try {
      await update(ref(database, taskPath), { materialLink: value, status: 'completed', completedAt: nowIso() });
      await saveMarketingLink(txId, item.linkKey || item.key, item.label || (item.key === 'brochures' ? 'Brochures' : 'Mailers'), value);
      await set(push(ref(database, `transactions/${txId}/tasks`)), newTask(item.review, 'Agent', { materialLink: value }));
      if (node) celebrateTaskDone(node, () => onChanged && onChanged());
      else if (onChanged) onChanged();
    } catch (err) {
      console.error('Could not send for approval', err);
      setError('Could not send. Please try again.');
    } finally { setBusy(false); }
  };

  // Agent or co-agent: approve, finish the review, and let the marketing manager know to order.
  const approve = async (e) => {
    if (busy) return;
    const node = e?.currentTarget || null;
    setBusy(true); setError('');
    try {
      await update(ref(database, taskPath), { status: 'completed', completedAt: nowIso(), approvedAt: nowIso() });
      await set(push(ref(database, `transactions/${txId}/tasks`)), newTask(item.order, 'Marketing Manager', { materialLink: task.materialLink || '' }));
      if (node) celebrateTaskDone(node, () => onChanged && onChanged());
      else if (onChanged) onChanged();
    } catch (err) {
      console.error('Could not approve', err);
      setError('Could not approve. Please try again.');
    } finally { setBusy(false); }
  };

  if (item.create.test(taskName)) {
    const ok = /^https?:\/\//i.test(link.trim());
    return (
      <div style={{ margin: '6px 0' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder={`Paste the ${item.key} link (https://...)`} style={input} />
          <button type="button" onClick={sendForApproval} disabled={!ok || busy} style={ok && !busy ? btn : btnOff}>Send to agent for approval</button>
        </div>
        {error && <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  if (item.reviewPattern.test(taskName)) {
    return (
      <div style={{ margin: '6px 0' }}>
        {task.materialLink && <div style={{ fontSize: 13, marginBottom: 6 }}>{item.label || (item.key === 'brochures' ? 'Brochures' : 'Mailers')}: <a href={task.materialLink} target="_blank" rel="noreferrer" style={{ color: '#8C6D1F', fontWeight: 600 }}>Open to review</a></div>}
        <button type="button" onClick={approve} disabled={busy} style={busy ? btnOff : btn}>Approve</button>
        {error && <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    );
  }

  // Marketing manager: the approved link, ready to order.
  return (
    <div style={{ margin: '6px 0', fontSize: 13 }}>
      {task.materialLink && <div><a href={task.materialLink} target="_blank" rel="noreferrer" style={{ color: '#8C6D1F', fontWeight: 600 }}>Open approved {item.key} to order</a></div>}
    </div>
  );
}
